// Every company's employees, roles and what each can sign in to — the same
// data a tenant admin sees on their Users page (CITS-Server
// services/tenantAccounts.service.js), read-only here apart from a reset link
// and the two revokes. Role and email changes stay with the company's admins.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  DevicePhoneMobileIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon,
  KeyIcon,
  MagnifyingGlassIcon,
  NoSymbolIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import api from '../../api/axios';

interface CompanySummary {
  id: string;
  company_code: string;
  company_name: string;
  status: string;
  users?: number;
  web_logins?: number;
  admins?: number;
  issues?: number;
  migration_pending?: boolean;
  error?: string;
}

interface Person {
  id: number;
  employee_id: string;
  name: string;
  role: string;
  email: string | null;
  is_active: boolean;
  lands_in: 'admin' | 'employee';
  field_app: { status: 'active' | 'revoked' | 'no_pin' };
  web: { status: 'active' | 'invited' | 'none' | 'revoked'; login_email: string | null; last_sign_in: string | null; two_factor: boolean; legacy_only?: boolean };
  admin_list: 'active' | 'inactive' | null;
  admin_list_role: string | null;
}

interface Issue {
  type: string;
  userId: number | null;
  email?: string;
}

const ISSUE_TEXT: Record<string, string> = {
  login_email_mismatch: 'Login email differs from the record',
  invite_not_used: 'Invited, password never set',
  archived_with_login: 'Archived, but the web login is still there',
  admin_list_role: "On the admin list, but shouldn't be",
  admin_list_role_mismatch: 'Admin list role differs from the Users page',
  admin_list_orphan: 'Admin list entry with no employee',
  duplicate_email: 'Shares an email with another employee',
  legacy_zitadel_login: 'Old Zitadel login — cannot sign in',
};

const WEB: Record<Person['web']['status'], [string, string]> = {
  active: ['Active', 'bg-emerald-100 text-emerald-700'],
  invited: ['Invited', 'bg-amber-100 text-amber-800'],
  none: ['None', 'bg-slate-100 text-slate-600'],
  revoked: ['Revoked', 'bg-red-100 text-red-700'],
};

function errText(err: unknown, fallback: string) {
  const e = err as { response?: { data?: { code?: string; message?: string } } };
  const code = e?.response?.data?.code;
  if (code === 'reauth_required') return 'Sign in again before doing this — revoking needs a fresh operator session.';
  if (code === 'two_factor_required') return 'Turn on two-step sign-in for your operator account before revoking access.';
  return e?.response?.data?.message || fallback;
}

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

export default function CompanyAccounts() {
  const [companies, setCompanies] = useState<CompanySummary[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [people, setPeople] = useState<Person[]>([]);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingCompany, setLoadingCompany] = useState(false);
  const [query, setQuery] = useState('');
  const [role, setRole] = useState('all');
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/super-admin/accounts');
      const list: CompanySummary[] = r.data?.companies ?? [];
      setCompanies(list);
      setSelected((cur) => cur ?? list.find((c) => !c.error)?.id ?? null);
    } catch (err) {
      setNotice({ ok: false, text: errText(err, 'Could not load companies.') });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCompany = useCallback(async (id: string) => {
    setLoadingCompany(true);
    try {
      const r = await api.get(`/super-admin/accounts/${id}`);
      setPeople(r.data?.users ?? []);
      setIssues(r.data?.issues ?? []);
    } catch (err) {
      setPeople([]);
      setIssues([]);
      setNotice({ ok: false, text: errText(err, "Could not load this company's accounts.") });
    } finally {
      setLoadingCompany(false);
    }
  }, []);

  useEffect(() => {
    void loadCompanies();
  }, [loadCompanies]);
  useEffect(() => {
    if (selected) void loadCompany(selected);
  }, [selected, loadCompany]);
  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 6000);
    return () => clearTimeout(t);
  }, [notice]);

  const company = companies.find((c) => c.id === selected);
  const issuesByUser = useMemo(() => {
    const m = new Map<number, Issue[]>();
    for (const i of issues) if (i.userId != null) m.set(i.userId, [...(m.get(i.userId) ?? []), i]);
    return m;
  }, [issues]);
  const orphans = issues.filter((i) => i.userId == null);

  const rows = people.filter((p) => {
    if (role !== 'all' && p.role !== role) return false;
    const q = query.trim().toLowerCase();
    return !q || `${p.name} ${p.email ?? ''} ${p.employee_id}`.toLowerCase().includes(q);
  });

  async function act(p: Person, path: string, label: string, confirm?: string) {
    if (!selected) return;
    if (confirm && !window.confirm(confirm)) return;
    const key = `${p.id}:${path}`;
    setBusy(key);
    try {
      await api.post(`/super-admin/accounts/${selected}/users/${p.id}/${path}`);
      setNotice({ ok: true, text: `${label} — ${p.name}.` });
      await loadCompany(selected);
    } catch (err) {
      setNotice({ ok: false, text: errText(err, `Could not complete: ${label.toLowerCase()}.`) });
    } finally {
      setBusy(null);
    }
  }

  const legacyCount = people.filter((p) => p.is_active && p.web.legacy_only).length;

  async function convertAll() {
    if (!selected) return;
    if (!window.confirm(
      `Convert ${legacyCount} account${legacyCount === 1 ? '' : 's'} at ${company?.company_name ?? 'this company'} to Better Auth?\n\n` +
      `Each person gets a real login and a set-password email. Nobody loses access — these accounts cannot sign in as they are.`
    )) return;
    setBusy('convert-all');
    try {
      const r = await api.post(`/super-admin/accounts/${selected}/convert-legacy-logins`);
      const { converted = 0, failed = 0 } = r.data ?? {};
      setNotice({
        ok: failed === 0,
        text: failed === 0
          ? `Converted ${converted} account${converted === 1 ? '' : 's'}. Set-password emails sent.`
          : `Converted ${converted}, but ${failed} failed — check the rows still flagged.`,
      });
      await loadCompany(selected);
    } catch (err) {
      setNotice({ ok: false, text: errText(err, 'Could not convert the accounts.') });
    } finally {
      setBusy(null);
    }
  }

  const roles = [...new Set(people.map((p) => p.role))].sort();

  return (
    <div className="space-y-5">
      {notice && (
        <div className={`rounded-lg border px-4 py-2.5 text-sm ${notice.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-200 bg-red-50 text-red-700'}`}>
          {notice.text}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {loading && !companies.length && <p className="text-sm text-slate-400">Loading companies…</p>}
        {companies.map((c) => (
          <button
            key={c.id}
            type="button"
            aria-pressed={c.id === selected}
            onClick={() => setSelected(c.id)}
            className={`rounded-xl border bg-white px-4 py-3 text-left transition-colors ${
              c.id === selected ? 'border-blue-500 ring-1 ring-blue-500' : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            <div className="font-semibold text-slate-900">{c.company_name}</div>
            {c.error ? (
              <div className="text-xs text-red-600">Couldn't read this company's database</div>
            ) : (
              <>
                <div className="flex gap-3 text-xs text-slate-500">
                  <span>{c.users} people</span>
                  <span>{c.web_logins} web logins</span>
                  <span>{c.admins} admins</span>
                </div>
                <div className={`text-xs ${c.issues ? 'text-amber-700' : 'text-slate-400'}`}>
                  {c.issues ? `${c.issues} need attention` : 'All accounts match'}
                  {c.migration_pending ? ' · web-login update not run' : ''}
                </div>
              </>
            )}
          </button>
        ))}
      </div>

      {company && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex min-w-[240px] flex-1 items-center gap-2 rounded-lg border border-slate-300 bg-white px-3">
              <MagnifyingGlassIcon className="h-4 w-4 text-slate-400" />
              <input
                id="company-accounts-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search ${company.company_name}`}
                className="w-full bg-transparent py-2 text-sm outline-none"
              />
            </label>
            <select id="company-accounts-role" aria-label="Role" value={role} onChange={(e) => setRole(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm">
              <option value="all">All roles</option>
              {roles.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => selected && loadCompany(selected)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm hover:bg-slate-50"
            >
              <ArrowPathIcon className={`h-4 w-4 ${loadingCompany ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
          <p className="text-xs text-slate-500">
            You can send a password reset link and revoke field-app or web access here. Role and email changes are made by{' '}
            {company.company_name}'s admins on their Users page.
          </p>

          {legacyCount > 0 && (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
              <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-amber-600" />
              <div className="min-w-0 flex-1 text-sm text-slate-800">
                <div className="font-semibold">
                  {legacyCount} account{legacyCount === 1 ? '' : 's'} left on the old Zitadel login
                </div>
                <div className="text-xs text-slate-600">
                  {legacyCount === 1 ? 'This person has' : 'These people have'} a portal account that points at Zitadel, which
                  nothing authenticates against any more. {legacyCount === 1 ? 'They cannot' : 'They cannot'} sign in, and a
                  password reset sends nothing. Converting creates a real login and emails a set-password link.
                </div>
              </div>
              <button
                type="button"
                disabled={!!busy}
                onClick={convertAll}
                className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
              >
                <ArrowPathIcon className={`h-4 w-4 ${busy === 'convert-all' ? 'animate-spin' : ''}`} />
                {busy === 'convert-all' ? 'Converting…' : `Convert ${legacyCount === 1 ? 'account' : 'all'}`}
              </button>
            </div>
          )}

          {orphans.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-slate-700">
              {orphans.map((o) => (
                <div key={`${o.type}-${o.email}`} className="flex gap-2">
                  <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 text-amber-600" /> {ISSUE_TEXT[o.type] ?? o.type}: {o.email}
                </div>
              ))}
            </div>
          )}

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full min-w-[980px] text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="px-3 py-2.5 text-left font-semibold">Person</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Role</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Field app</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Web login</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Admin list</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Last web sign-in</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const probs = issuesByUser.get(p.id) ?? [];
                  const hasLogin = p.web.status === 'active' || p.web.status === 'invited';
                  return (
                    <tr key={p.id} className={`border-t border-slate-100 align-top ${p.is_active ? '' : 'opacity-60'}`}>
                      <td className="px-3 py-2.5">
                        <div className="font-semibold text-slate-900">
                          {p.name}
                          {!p.is_active && <span className="ml-2 text-[11px] font-medium text-slate-500">archived</span>}
                        </div>
                        <div className="text-xs text-slate-400">
                          {p.employee_id} · {p.email || 'no email'}
                        </div>
                        {probs.map((i) => (
                          <div key={i.type} className="mt-1 flex items-center gap-1 text-xs text-amber-700">
                            <ExclamationTriangleIcon className="h-3.5 w-3.5" /> {ISSUE_TEXT[i.type] ?? i.type}
                          </div>
                        ))}
                      </td>
                      <td className="px-3 py-2.5 capitalize text-slate-700">{p.role}</td>
                      <td className="px-3 py-2.5">
                        <span className="inline-flex items-center gap-1 text-slate-700">
                          <DevicePhoneMobileIcon className="h-4 w-4 text-slate-400" />
                          {p.field_app.status === 'active' ? 'On' : p.field_app.status === 'revoked' ? <span className="text-red-700">Revoked</span> : 'No PIN'}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${WEB[p.web.status][1]}`}>{WEB[p.web.status][0]}</span>
                        {hasLogin && (
                          <div className="mt-0.5 text-xs text-slate-400">
                            <GlobeAltIcon className="mr-1 inline h-3.5 w-3.5" />
                            {p.lands_in === 'admin' ? 'Admin portal' : 'Employee portal'}
                            {p.web.two_factor ? ' · two-step on' : ''}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-slate-600">
                        {p.admin_list === 'active' ? `Listed${p.admin_list_role ? ` as ${p.admin_list_role}` : ''}` : p.admin_list === 'inactive' ? 'Removed' : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-slate-600">{when(p.web.last_sign_in)}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex flex-wrap justify-end gap-1.5">
                          {p.web.legacy_only && (
                            <button
                              type="button"
                              disabled={!!busy}
                              onClick={() => act(p, 'convert-login', 'Converted to a Better Auth login, set-password email sent')}
                              className="inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                            >
                              <ArrowPathIcon className="h-3.5 w-3.5" /> Convert login
                            </button>
                          )}
                          {hasLogin && (
                            <button
                              type="button"
                              disabled={!!busy}
                              onClick={() => act(p, 'password-reset', `Password reset link sent to ${p.web.login_email}`)}
                              className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-50"
                            >
                              <KeyIcon className="h-3.5 w-3.5" /> Reset link
                            </button>
                          )}
                          {hasLogin && (
                            <button
                              type="button"
                              disabled={!!busy}
                              onClick={() =>
                                act(p, 'web-login/revoke', 'Web login revoked', `Revoke ${p.name}'s web login at ${company.company_name}? They're signed out and can't sign back in until the company's admins give access again.`)
                              }
                              className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50"
                            >
                              <NoSymbolIcon className="h-3.5 w-3.5" /> Revoke web
                            </button>
                          )}
                          {p.field_app.status === 'revoked' ? (
                            <button
                              type="button"
                              disabled={!!busy}
                              onClick={() => act(p, 'field-app/restore', 'Field-app access restored')}
                              className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-50"
                            >
                              <CheckCircleIcon className="h-3.5 w-3.5" /> Restore app
                            </button>
                          ) : (
                            <button
                              type="button"
                              disabled={!!busy}
                              onClick={() =>
                                act(p, 'field-app/revoke', 'Field-app access revoked', `Revoke ${p.name}'s field-app access at ${company.company_name}? They can't sign in to CITS Time on a phone or kiosk, and a signed-in phone stops syncing within about 15 minutes.`)
                              }
                              className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50"
                            >
                              <NoSymbolIcon className="h-3.5 w-3.5" /> Revoke app
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!rows.length && (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center text-slate-400">
                      {loadingCompany ? 'Loading…' : 'No one matches.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
